import type { CheckoutData } from '@/types/checkout';
import { Routes } from './routes';
import { getCheckoutRequestOptions } from '@/utils/checkout';
import { getEnvVariable } from '@/utils/get-env-variable';
import {
  getSessionIdFromUrlParams,
  isApplePayTabMode,
  isSubscriptionRoute,
  shouldRenderPaymentHandlerPage,
} from '@/utils/location';
import { SESSION_ID } from '@/constants/params';
import { extractRedirectUrlFromApiError, persistRedirectUrl } from '@/utils/persistedRedirectUrl';

/**
 * Pre-flight `/checkout/v3` from `main.tsx` — fires the same fetch that
 * `useCheckoutData`'s queryFn would later issue, but kicks off at module-
 * eval time (parallel with React tree mount + boot decode) instead of
 * after the first effect cycle inside `<CheckoutContent>`.
 *
 * Why this is a critical-path win: `useCheckoutData` is gated on
 * `isApiInitilized`, which only flips inside `CheckoutContent`'s
 * `useEffect(initilizeCheckout)`. The GET therefore cannot start until
 *   (a) the FE bundle has parsed (~830ms in measured staging runs), AND
 *   (b) React has hydrated, AND
 *   (c) the first effect cycle has run.
 * That serial gap is the dominant slice of `t3 − t2` for FTD, last-
 * method, and single-payment templates alike (all three render after
 * `/checkout/v3` resolves and `notifyCheckoutOpened` fires).
 *
 * Zero-regression contract:
 *   - If we can't safely build a request at module-eval time (no
 *     session token, payment-handler route, SSR), we don't fire and
 *     `consumePrefetch()` returns null. The original `apiClient.get`
 *     path inside `useCheckoutData.queryFn` runs unchanged.
 *   - If the prefetch errors at any stage (network, non-2xx, JSON
 *     parse), `consumePrefetch()` still hands back the rejected
 *     promise — the queryFn catches that and falls THROUGH to the
 *     `apiClient.get` path so the legacy "return {} on error" contract
 *     is preserved.
 *   - Headers, URL, credentials, method, mode are byte-identical to
 *     what `apiClient.request('/checkout/v3', 'GET', getCheckoutRequestOptions())`
 *     produces (same `getCheckoutRequestOptions`, same `Routes.CHECKOUT`,
 *     same `VITE_API_URL` resolution via `getEnvVariable`, same
 *     `Authorization: Bearer <sessionToken>` shape set by
 *     `apiClient.initilize`).
 *   - One-shot. Subsequent React Query invalidations / refetches go
 *     through `apiClient` normally, so any auth-rotation or header
 *     mutation done by callers between renders is honored.
 */
let prefetchPromise: Promise<CheckoutData> | null = null;
let prefetchConsumed = false;

/**
 * Resolves the session token the same way `CheckoutContent.initilizeCheckout`
 * does: `?session-id=` query param first (used by some embedded flows),
 * then the path-based `/<sessionId>` token. Returns `null` if neither is
 * present so the caller can short-circuit cleanly.
 */
const getSessionToken = (): string | null => {
  try {
    const search = new URLSearchParams(window.location.search);
    return search.get(SESSION_ID) || getSessionIdFromUrlParams() || null;
  } catch {
    return null;
  }
};

export const kickoffCheckoutDataPrefetch = (): void => {
  if (prefetchPromise) return;
  if (typeof window === 'undefined') return;
  // `/handle-payment/*` is the Stripe redirect-back route — it renders
  // `<StripePaymentHandlerPage>` and never calls `/checkout/v3`. Doing a
  // useless prefetch here would also leak Authorization with the wrong
  // (non-session) path token in `getSessionIdFromUrlParams()`.
  if (shouldRenderPaymentHandlerPage()) return;
  // The Apple Pay fallback tab receives the already-fetched `/checkout/v3`
  // payload from its opener via postMessage and must NOT re-issue the call
  // (see `useApplePayTabBootstrap`). Its queryFn only fetches as a last
  // resort when the opener handshake fails.
  if (isApplePayTabMode()) return;
  // Subscriptions run on their own session store and never read `/checkout/v3`
  // (`SubscriptionCheckoutContent` fetches `/checkout/subscriptions`). Firing
  // it here only spends a request that the gateway answers with a 401.
  if (isSubscriptionRoute()) return;

  const sessionToken = getSessionToken();
  if (!sessionToken) return;

  // Build the exact URL `apiClient` would build. `getEnvVariable` with
  // the same default keeps prod/staging/sandbox/local in lock-step
  // with the apiClient singleton.
  const baseUrl: string = getEnvVariable('VITE_API_URL', 'https://api.appcharge.com').replace(/\/$/, '');
  const url = `${baseUrl}${Routes.CHECKOUT}`;

  // Mirror `apiClient.request` header merge: defaults first, then
  // per-request additions. `apiClient` defaults to `Content-Type:
  // application/json`; `apiClient.initilize` sets the Authorization
  // header (we set it directly here since `apiClient` isn't initialized
  // yet at module-eval time). `getCheckoutRequestOptions()` adds the
  // `x-source-version` / `x-sdk-version` / `x-client-platform` headers
  // — read from URL query params + UA, so available at module-eval.
  const requestOptions = getCheckoutRequestOptions();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${sessionToken}`,
    ...requestOptions.headers,
  };

  prefetchPromise = fetch(url, {
    method: 'GET',
    headers,
    credentials: 'include',
    mode: 'cors',
  }).then(async (response) => {
    if (!response.ok) {
      // Same contract as `apiClient.parseErrorBody`: read the stream once as
      // text, then JSON.parse. `response.json()` consumes the body, so a
      // non-JSON error (e.g. an HTML 502) cannot be re-read and `info` is lost.
      let errorBody: unknown;
      try {
        const raw = await response.text();
        try {
          errorBody = JSON.parse(raw);
        } catch {
          errorBody = raw;
        }
      } catch {
        errorBody = undefined;
      }
      persistRedirectUrl(extractRedirectUrlFromApiError({ info: errorBody }));
      // Throw with the same shape `apiClient.request` would, so the
      // queryFn fall-through catches it identically.
      const error = new Error(`HTTP ${response.status}`) as Error & { status?: number; info?: unknown };
      error.status = response.status;
      error.info = errorBody;
      throw error;
    }
    const checkoutData = (await response.json()) as CheckoutData;
    persistRedirectUrl(checkoutData.redirectUrl);
    return checkoutData;
  });
};

/**
 * One-shot consumer for `useCheckoutData`'s queryFn. Returns the in-
 * flight (or resolved) prefetch promise the FIRST time it's called;
 * `null` on every subsequent call.
 *
 * One-shot semantics matter because React Query's `staleTime: Infinity`
 * means the queryFn runs once per query lifecycle — but on manual
 * `refetch()` / invalidate it runs again, and that second run MUST go
 * through the regular `apiClient` path to get fresh data, not replay
 * a stale prefetch promise.
 */
export const consumePrefetch = (): Promise<CheckoutData> | null => {
  if (prefetchConsumed) return null;
  if (!prefetchPromise) return null;
  prefetchConsumed = true;
  return prefetchPromise;
};

/**
 * Non-consuming peek at the in-flight prefetch promise. Used by side-
 * effects that want to piggyback on the prefetch (e.g. PSP prewarm)
 * without claiming it for the queryFn — the queryFn must still get the
 * promise via `consumePrefetch()`.
 *
 * Returns `null` if the prefetch was never kicked off (payment-handler
 * route, no session token) so callers can skip silently.
 */
export const peekPrefetch = (): Promise<CheckoutData> | null => {
  return prefetchPromise;
};

/**
 * Test-only reset. Production code never calls this; it exists so unit
 * tests of consumers can run with a clean module-scope state between
 * cases without resorting to `vi.resetModules()`.
 */
export const __resetCheckoutDataPrefetchForTests = (): void => {
  prefetchPromise = null;
  prefetchConsumed = false;
};
